import type { ExchangeSnapshot } from "./sqlOnlyActionSnapshotUtils";

/** sessionStorage prefix — cleared when the tab closes. */
export const SQL_ONLY_ACTION_SCAN_STORAGE_PREFIX =
  "ads.exchanges.actionScan.v1.";

/** Discard persisted scans older than this (stale workflow rows). */
export const SQL_ONLY_ACTION_SCAN_STORAGE_TTL_MS = 30 * 60 * 1000;

/** Skip persistence when the payload would exceed this (sessionStorage ~5MB). */
export const SQL_ONLY_ACTION_SCAN_STORAGE_MAX_BYTES = 2_000_000;

export interface PersistedSqlOnlyActionScan {
  version: 1;
  savedAt: number;
  odataSkip: number;
  odataRowsScanned: number;
  chunkRequests?: number;
  odataExhausted: boolean;
  scanTruncated: boolean;
  snapshots: Array<[string, ExchangeSnapshot]>;
}

export interface SqlOnlyActionScanPersistableState {
  snapshots: Map<string, ExchangeSnapshot>;
  odataSkip: number;
  odataRowsScanned: number;
  chunkRequests: number;
  odataExhausted: boolean;
  scanTruncated: boolean;
}

const storageKeyForScan = (scanKey: string): string =>
  `${SQL_ONLY_ACTION_SCAN_STORAGE_PREFIX}${encodeURIComponent(scanKey)}`;

const isSessionStorageAvailable = (): boolean =>
  typeof sessionStorage !== "undefined";

export const serializeSqlOnlyActionScanState = (
  state: SqlOnlyActionScanPersistableState,
): PersistedSqlOnlyActionScan => ({
  version: 1,
  savedAt: Date.now(),
  odataSkip: state.odataSkip,
  odataRowsScanned: state.odataRowsScanned,
  chunkRequests: state.chunkRequests,
  odataExhausted: state.odataExhausted,
  scanTruncated: state.scanTruncated,
  snapshots: Array.from(state.snapshots.entries()),
});

export const hydrateSqlOnlyActionScanState = (
  scanKey: string,
): SqlOnlyActionScanPersistableState | null => {
  if (!isSessionStorageAvailable()) {
    return null;
  }

  try {
    const raw = sessionStorage.getItem(storageKeyForScan(scanKey));
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as PersistedSqlOnlyActionScan;
    if (parsed?.version !== 1 || !Array.isArray(parsed.snapshots)) {
      return null;
    }

    if (Date.now() - parsed.savedAt > SQL_ONLY_ACTION_SCAN_STORAGE_TTL_MS) {
      sessionStorage.removeItem(storageKeyForScan(scanKey));
      return null;
    }

    return {
      snapshots: new Map(parsed.snapshots),
      odataSkip: parsed.odataSkip,
      odataRowsScanned: parsed.odataRowsScanned,
      chunkRequests: parsed.chunkRequests ?? 0,
      odataExhausted: parsed.odataExhausted,
      scanTruncated: parsed.scanTruncated,
    };
  } catch {
    return null;
  }
};

export const persistSqlOnlyActionScanState = (
  scanKey: string,
  state: SqlOnlyActionScanPersistableState,
): void => {
  if (!isSessionStorageAvailable()) {
    return;
  }

  try {
    const payload = serializeSqlOnlyActionScanState(state);
    const serialized = JSON.stringify(payload);
    if (serialized.length > SQL_ONLY_ACTION_SCAN_STORAGE_MAX_BYTES) {
      return;
    }
    sessionStorage.setItem(storageKeyForScan(scanKey), serialized);
  } catch {
    // Quota exceeded or private browsing — ignore.
  }
};

export const clearPersistedSqlOnlyActionScan = (scanKey: string): void => {
  if (!isSessionStorageAvailable()) {
    return;
  }
  try {
    sessionStorage.removeItem(storageKeyForScan(scanKey));
  } catch {
    // ignore
  }
};

export const clearAllPersistedSqlOnlyActionScans = (): void => {
  if (!isSessionStorageAvailable()) {
    return;
  }
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(SQL_ONLY_ACTION_SCAN_STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // ignore
  }
};
